/**
 * uiScale.ts —— **整体界面缩放**：让"窗体比例相同时，看到的版面相同"。
 *
 * ## 为什么是"整体 zoom"而不是"各组件自适应"
 *
 * 用户实测反馈：桌面端窗口比全屏小、14 寸笔记本上更明显，而此前只有左侧章节列表会在
 * 一个断点上突然变窄，其他组件（按钮、内边距、字号、面板宽度…）**完全不随窗体变化** ——
 * 结果同样是 16:10 的窗口，大屏上看到的内容与笔记本上完全不同。
 *
 * 要做到"比例相同则内容相同"，只有一条路：**把整个界面等比缩放**（CSS `zoom`）——
 * 字号、按钮、间距、面板宽度一起缩，行宽（每行多少个字）才保持不变。
 * 逐个组件写媒体查询做不到这一点（那只会改变"谁宽谁窄"，不会改变内容密度）。
 *
 * ## 缩放系数怎么算
 *
 * `min(视口宽 / 设计宽, 视口高 / 设计高)` —— 取宽高两个方向的**较小值**，
 * 于是：比例相同的窗口得到同一个系数（这正是需求）；窗口变矮时也不会纵向溢出。
 * 再夹在 `AUTO_SCALE_MIN ~ AUTO_SCALE_MAX` 之间：太小看不清，太大反而显示的内容更少。
 *
 * ## 与"界面大小"设置的关系
 *
 * 设置页那个 4 档（较小/标准/较大/很大）现在是**乘在自动系数之上的偏好**：
 * `最终 zoom = 自动系数 × 用户档位`。用户想更大一点就调它，与自动适配不冲突。
 *
 * ## 生效范围
 *
 * zoom 挂在 `#root` 上（不是 `html`/`body`）：portal 到 `document.body` 的弹窗/菜单
 * **不受缩放**，它们用 `getBoundingClientRect()`（视觉像素）算 `position: fixed` 坐标，
 * 若跟着缩放反而会错位 —— 代价只是弹窗尺寸不随缩放变化，比错位好得多。
 *
 * 缩放后**布局像素 ≠ 视觉像素**，需要换算的两处已单独处理：
 * 拖拽手柄的指针增量（`ResizeHandle`）、自动增高输入框（`useAutoGrowTextarea`）。
 */

const STORAGE_KEY = 'ui-font-scale';
const AUTO_STORAGE_KEY = 'xiaotianshu:auto-ui-scale:v1';

/** 主窗口发布、副窗体跟随的**最终缩放系数**（同源共享 localStorage） */
const SHARED_ZOOM_KEY = 'xiaotianshu:ui-zoom:v1';

/** 设计基准尺寸：界面是按这个尺寸设计的（16:10 笔记本） */
export const DESIGN_WIDTH = 1440;
export const DESIGN_HEIGHT = 900;

/** 自动缩放的上下限 */
export const AUTO_SCALE_MIN = 0.7;
export const AUTO_SCALE_MAX = 1.25;

export interface UiFontScaleOption {
  value: number;
  label: string;
}

/**
 * 用户偏好档位 —— **乘在自动系数之上**。
 * 标签措辞刻意从"界面文字大小"改成"界面大小"：它缩的不只是字。
 */
export const UI_FONT_SCALE_OPTIONS: UiFontScaleOption[] = [
  { value: 0.9, label: '较小' },
  { value: 1, label: '标准' },
  { value: 1.1, label: '较大' },
  { value: 1.25, label: '很大' },
];

// ── 自动缩放（纯函数，便于单测）────────────────────────────────────────

/**
 * 按视口算出自动缩放系数。
 *
 * 取宽高两个方向的较小比例，所以**比例相同的窗口系数相同** —— 这正是"内容尽量相同"的要求。
 * 无效输入退回设计尺寸（系数 1）。
 */
export function computeAutoUiScale(width: number, height: number): number {
  const w = Number.isFinite(width) && width > 0 ? width : DESIGN_WIDTH;
  const h = Number.isFinite(height) && height > 0 ? height : DESIGN_HEIGHT;
  const raw = Math.min(w / DESIGN_WIDTH, h / DESIGN_HEIGHT);
  const clamped = Math.min(AUTO_SCALE_MAX, Math.max(AUTO_SCALE_MIN, raw));
  // 保留两位小数：避免拖窗口时每像素都写一次样式（值抖动但视觉无差别）
  return Math.round(clamped * 100) / 100;
}

/** 最终 zoom = 自动系数（可关）× 用户档位 */
export function effectiveUiZoom(autoEnabled: boolean, autoScale: number, userScale: number): number {
  const auto = autoEnabled ? autoScale : 1;
  return Math.round(auto * userScale * 100) / 100;
}

// ── 存取 ───────────────────────────────────────────────────────────────

/**
 * 用户偏好的**模块级缓存**。
 *
 * 为什么缓存：窗口 resize 时缩放要**逐帧同步**重算（见 `App.tsx` 的 `useAutoUiZoom`），
 * 每次都读 localStorage 没必要（同步 API，拖动时每帧都读会白白增加开销）。
 * `setUiFontScale` / `setAutoUiScaleEnabled` 会同步更新缓存。
 */
let cachedUserScale: number | null = null;
let cachedAutoEnabled: boolean | null = null;

export function getUiFontScale(): number {
  if (cachedUserScale !== null) return cachedUserScale;
  try {
    const v = Number(localStorage.getItem(STORAGE_KEY));
    cachedUserScale = UI_FONT_SCALE_OPTIONS.some(o => o.value === v) ? v : 1;
  } catch {
    cachedUserScale = 1;
  }
  return cachedUserScale;
}

/** 自动缩放是否开启（默认开 —— 这是用户要的默认行为） */
export function getAutoUiScaleEnabled(): boolean {
  if (cachedAutoEnabled !== null) return cachedAutoEnabled;
  try {
    cachedAutoEnabled = localStorage.getItem(AUTO_STORAGE_KEY) !== '0';
  } catch {
    cachedAutoEnabled = true;
  }
  return cachedAutoEnabled;
}

export function setAutoUiScaleEnabled(on: boolean): void {
  cachedAutoEnabled = on;
  try {
    localStorage.setItem(AUTO_STORAGE_KEY, on ? '1' : '0');
  } catch {
    /* 存储不可用时仅本次会话生效 */
  }
  applyAutoUiZoom();
}

export function setUiFontScale(scale: number): void {
  cachedUserScale = scale;
  try {
    localStorage.setItem(STORAGE_KEY, String(scale));
  } catch {
    /* 存储不可用时仅本次会话生效 */
  }
  applyAutoUiZoom();
}

// ── 生效 ───────────────────────────────────────────────────────────────

/**
 * 当前生效的 zoom。
 *
 * 模块级缓存：**布局像素与视觉像素的换算要用它**（拖拽增量、自动增高），
 * 每次都去 getComputedStyle 读太贵，也没必要。
 */
let currentZoom = 1;

export function currentUiZoom(): number {
  return currentZoom;
}

/**
 * 当前窗口的**布局空间宽度**（视觉像素 ÷ 缩放）。
 *
 * 布局里的宽度（侧栏、面板、编辑区）都活在这个坐标系里，所以"还剩多少余地"
 * 必须用这个值判断 —— 直接用 `innerWidth` 会在缩放后算错。
 *
 * 量化到 8px：窗口 resize 时逐帧同步调用它更新 React 状态，
 * 值不变就跳过重渲染（拖动窗口不会每帧重渲染整个创作页）。
 */
export function currentLayoutWidth(): number {
  const w = typeof window === 'undefined' ? DESIGN_WIDTH : (window.innerWidth || DESIGN_WIDTH);
  const z = currentUiZoom();
  return Math.round(w / z / 8) * 8;
}

/** 把最终系数写进 CSS 变量（`#root` 的 zoom 引用）；返回实际生效值 */
export function applyUiZoom(zoom: number): number {
  const z = Number.isFinite(zoom) && zoom > 0 ? zoom : 1;
  if (z === currentZoom) return z;      // 值没变就别碰 DOM（拖动窗口时逐帧调用）
  currentZoom = z;
  try {
    document.documentElement.style.setProperty('--ui-zoom', String(z));
  } catch {
    /* 非浏览器环境（单测）忽略 */
  }
  return z;
}

/**
 * 当前窗口是不是**副窗体/独立窗口**（只渲染单个面板的那一窗）。
 *
 * 判据有二（任一成立即可）：
 *  1. `window.__XTS_PANE__` —— Rust **只给副窗口**注入的标记（见 `windowhost.rs`）；
 *  2. 路由上带 `?solo=<面板>` —— HashRouter，所以查 `hash`（见 `Editor/index.tsx`）。
 */
export function isSatelliteWindow(): boolean {
  if (typeof window === 'undefined') return false;
  if ((window as unknown as { __XTS_PANE__?: string }).__XTS_PANE__) return true;
  return /[?&]solo=/.test(window.location?.hash || '');
}

/**
 * **纯决策**：当前窗口该用哪个缩放系数、以及是否要发布出去。
 *
 * 副窗体必须**跟随主窗口**，这是用户实测反馈的缺陷：
 * 副窗体是独立 SPA 实例、按自己的视口算缩放，而它只有几百像素宽 →
 * 直接掉到下限 0.7，于是同一个面板在副窗体里的字号明显小于主窗口。
 *
 * 规则：
 *  - **副窗体**：优先用主窗口发布的值（那是**最终**系数，不再乘用户档位，
 *    否则会重复相乘）；主窗口还没发布过（副窗体先开）就按**屏幕**算 —— 反正
 *    绝不能按自己那点宽度算；
 *  - **主窗口 / 网页端**：按自己的视口算，并**发布**出去供副窗体跟随。
 */
export function resolveWindowUiZoom(input: {
  isSatellite: boolean;
  sharedZoom: number | null;
  viewportWidth: number;
  viewportHeight: number;
  screenWidth: number;
  screenHeight: number;
  autoEnabled: boolean;
  userScale: number;
}): { zoom: number; publish: boolean } {
  const {
    isSatellite, sharedZoom, viewportWidth, viewportHeight,
    screenWidth, screenHeight, autoEnabled, userScale,
  } = input;

  if (isSatellite) {
    if (Number.isFinite(sharedZoom) && (sharedZoom as number) > 0) {
      return { zoom: (sharedZoom as number), publish: false };
    }
    const auto = autoEnabled ? computeAutoUiScale(screenWidth, screenHeight) : 1;
    return { zoom: effectiveUiZoom(true, auto, userScale), publish: false };
  }

  const auto = autoEnabled ? computeAutoUiScale(viewportWidth, viewportHeight) : 1;
  return { zoom: effectiveUiZoom(true, auto, userScale), publish: true };
}

function readSharedUiZoom(): number | null {
  try {
    const raw = localStorage.getItem(SHARED_ZOOM_KEY);
    if (raw === null) return null;
    const z = Number(raw);
    return Number.isFinite(z) && z > 0 ? z : null;
  } catch {
    return null;
  }
}

function writeSharedUiZoom(zoom: number): void {
  try {
    localStorage.setItem(SHARED_ZOOM_KEY, String(zoom));
  } catch {
    /* 存储不可用时副窗体退回按屏幕算 */
  }
}

/**
 * 按当前窗口的视口/屏幕与用户偏好重新计算并应用。
 *
 * 启动时、以及窗口尺寸变化时调用（见 `App.tsx` 的 `useAutoUiZoom`）。
 */
export function applyAutoUiZoom(): number {
  // 手机 /m 是独立移动版式，不能套桌面端的等比缩小；否则 16px 正文会被 0.7 缩成 11px。
  if (typeof window !== 'undefined' && /^#\/m(?:$|[/?])/.test(window.location.hash)) {
    return applyUiZoom(1);
  }
  const hasWindow = typeof window !== 'undefined';
  const viewportWidth = hasWindow ? (window.innerWidth || DESIGN_WIDTH) : DESIGN_WIDTH;
  const viewportHeight = hasWindow ? (window.innerHeight || DESIGN_HEIGHT) : DESIGN_HEIGHT;
  const screenWidth = hasWindow ? (window.screen?.availWidth || viewportWidth) : DESIGN_WIDTH;
  const screenHeight = hasWindow ? (window.screen?.availHeight || viewportHeight) : DESIGN_HEIGHT;

  const { zoom, publish } = resolveWindowUiZoom({
    isSatellite: isSatelliteWindow(),
    sharedZoom: readSharedUiZoom(),
    viewportWidth,
    viewportHeight,
    screenWidth,
    screenHeight,
    autoEnabled: getAutoUiScaleEnabled(),
    userScale: getUiFontScale(),
  });

  if (publish) writeSharedUiZoom(zoom);
  return applyUiZoom(zoom);
}

/**
 * 副窗体跟随主窗口的缩放：主窗口 resize 后会发布新的系数，
 * 这里收到 `storage` 事件就跟着变（`focus` 兜底 —— 某些宿主不派发跨窗口 storage 事件）。
 *
 * 主窗口不需要监听（它是发布方）。
 */
export function watchSharedUiZoom(): void {
  if (typeof window === 'undefined' || !isSatelliteWindow()) return;

  const sync = () => {
    const z = readSharedUiZoom();
    if (z !== null) applyUiZoom(z);
  };

  window.addEventListener('storage', (e) => {
    if (e.key === null || e.key === SHARED_ZOOM_KEY) sync();
  });
  window.addEventListener('focus', sync);
  sync();
}

/**
 * 兼容旧调用点（创作页启动时曾只缩放自己那一页）。
 * 现在缩放是全局的，这里保留同名导出以免漏改的调用点报错。
 */
export function applyUiFontScale(_scale?: number): void {
  applyAutoUiZoom();
}
